import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Solicitacao } from '@eleg360/types';
import { solicitacoesApi } from '@/lib/api/solicitacoes';
import { criarComFilaOffline } from '@/lib/hooks/useCriacaoOffline';
import { contatosApi } from '@/lib/api/contatos';
import { liderancasApi } from '@/lib/api/liderancas';
import { adminApi } from '@/lib/api/admin';
import { reguasApi, GATILHO_SOLICITACAO_CONCLUIDA } from '@/lib/api/reguas';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { useTemas } from '@/lib/hooks/useTemas';
import { useOffline } from '@/lib/hooks/useOffline';
import { podeEnviarWhatsApp, motivoBloqueio } from '@/lib/lgpd';
import { toast } from '@/lib/stores/toastStore';
import { formatarData } from '@/lib/format';
import { exportarCsv } from '@/lib/csv';
import { Anexos } from '../anexos/Anexos';
import { Drawer } from '@/design-system/Drawer';
import { acoesApi } from '@/lib/api/acoes';
import { PainelSolicitacoes, type FiltroPainel } from './PainelSolicitacoes';
import { Info } from '@/design-system/Info';

export type Status = 'nova' | 'em_analise' | 'encaminhada' | 'concluida';
type Prioridade = 'baixa' | 'media' | 'alta' | 'urgente';

/**
 * Os quatro status e seus rótulos. Exportado porque o funil dos painéis
 * (`PainelSolicitacoes`) precisa exatamente destes valores: mantidos em duas
 * listas, funil e Kanban rotulariam o mesmo status de formas diferentes na
 * primeira vez que uma delas mudasse.
 */
export const COLUNAS: Array<{ v: Status; l: string }> = [
  { v: 'nova', l: 'Recebida' },
  { v: 'em_analise', l: 'Em análise' },
  { v: 'encaminhada', l: 'Encaminhada' },
  { v: 'concluida', l: 'Concluída' },
];

const PRIORIDADES: Array<{ v: Prioridade; l: string; chip: string }> = [
  { v: 'baixa', l: 'Baixa', chip: 'neutral' },
  { v: 'media', l: 'Média', chip: 'neutral' },
  { v: 'alta', l: 'Alta', chip: 'warn' },
  { v: 'urgente', l: 'Urgente', chip: 'crit' },
];

const CANAIS = ['presencial', 'whatsapp', 'telefone', 'email', 'online'] as const;

/**
 * As cinco faixas de prazo do protótipo (proto:1197). Não é um booleano de
 * "vencida": "vencem hoje" e "vencem em 3 dias" são o que faz a triagem do dia
 * caber numa tela, e "concluídas" é o único que ignora o relógio.
 */
const PRAZOS: Array<{ v: string; l: string }> = [
  { v: 'vencida', l: 'Vencidas' },
  { v: 'hoje', l: 'Vencem hoje' },
  { v: '3d', l: 'Vencem em 3 dias' },
  { v: 'ok', l: 'No prazo' },
  { v: 'conc', l: 'Concluídas' },
];

/** Quantas linhas por página na visão Lista (proto:1184, `PP=15`). */
const POR_PAGINA = 15;

const rotuloStatus = (s: Status) => COLUNAS.find((c) => c.v === s)?.l || s;
const chipPrioridade = (p: Prioridade) => PRIORIDADES.find((x) => x.v === p)?.chip || 'neutral';

/** Dias restantes até o prazo — negativo quando já venceu. */
const diasDeSla = (prazo?: string) => {
  if (!prazo) return null;
  const d = new Date(prazo);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
};

export function SolicitacoesPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  /**
   * Filtros vindos da URL, para o painel poder abrir esta tela ja recortada.
   *
   * O painel mostra "12 abertas" e "3 com SLA vencido"; sem isso, clicar leva a
   * lista inteira e o usuario refaz o filtro na mao para chegar aos mesmos
   * registros que acabou de ver.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const recorte = searchParams.get('recorte') ?? '';

  const [view, setView] = useState<'kanban' | 'lista' | 'paineis'>(recorte ? 'lista' : 'kanban');
  const [q, setQ] = useState('');
  const [ufFiltro, setUfFiltro] = useState('');
  const [municipioFiltro, setMunicipioFiltro] = useState('');
  const [bairroFiltro, setBairroFiltro] = useState(searchParams.get('bairro') ?? '');
  const [prioridadeFiltro, setPrioridadeFiltro] = useState('');
  // Recortes que os painéis escrevem ao serem clicados — e que o usuário
  // também consegue ver e limpar nos selects da barra de filtros.
  const [temaFiltro, setTemaFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [responsavelFiltro, setResponsavelFiltro] = useState('');
  const [prazoFiltro, setPrazoFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  /**
   * `null` = a ordem em que o servidor devolveu (`created_at DESC`). Começar
   * com uma ordenação do cliente esconderia qual é o critério do backend.
   */
  const [ordem, setOrdem] = useState<{ campo: string; dir: 1 | -1 } | null>(null);

  const { online } = useOffline();

  const [selecionada, setSelecionada] = useState<Solicitacao | null>(null);
  const [novaAberta, setNovaAberta] = useState(false);
  const [comentario, setComentario] = useState('');

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const filtros = {
    search: q || undefined,
    bairro: bairroFiltro || undefined,
    prioridade: prioridadeFiltro || undefined,
  };

  /**
   * Selects de território EM CASCATA (proto:1191-1193).
   *
   * As UFs e os municípios saem dos territórios cadastrados (`useBairros`
   * expõe `uf` e `municipio`) — não de uma lista própria da tela. O protótipo
   * cravava um `UFS`; uma constante nova aqui ofereceria as 27 unidades da
   * federação num mandato que atua em uma.
   *
   * A cascata não é enfeite: sem ela dá para escolher "Icaraí" com o estado do
   * Ceará e olhar uma lista vazia sem entender por quê.
   */
  const ufs = [...new Set(bairros.map((b) => b.uf).filter(Boolean))].sort();
  const municipios = [
    ...new Set(
      bairros.filter((b) => !ufFiltro || b.uf === ufFiltro).map((b) => b.municipio).filter(Boolean),
    ),
  ].sort();
  const territorios = bairros.filter(
    (b) =>
      (!ufFiltro || b.uf === ufFiltro) && (!municipioFiltro || b.municipio === municipioFiltro),
  );

  /**
   * Qualquer troca de filtro devolve a paginação ao início. Sem isso, quem
   * estava na página 3 e recorta para um conjunto de 4 registros fica olhando
   * uma tabela vazia — o mesmo defeito que o `page:1` do "Limpar" evita.
   */
  const aoFiltrar = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPagina(1);
  };

  const trocarUf = (v: string) => {
    setUfFiltro(v);
    // Trocar a UF limpa município E território (proto:1191).
    setMunicipioFiltro('');
    setBairroFiltro('');
    setPagina(1);
  };

  const trocarMunicipio = (v: string) => {
    setMunicipioFiltro(v);
    // Trocar o município limpa o território (proto:1192).
    setBairroFiltro('');
    setPagina(1);
  };

  /**
   * "Limpar" zera os NOVE campos do protótipo, a página inclusive (proto:1198)
   * — quem esquece o `page:1` deixa o usuário numa página que o recorte maior
   * não tem mais.
   *
   * Prioridade entra como décimo: esta tela tem um filtro de prioridade que o
   * protótipo não tinha, e um botão "Limpar" que deixasse um filtro ligado
   * estaria mentindo no próprio rótulo.
   *
   * O `recorte` da URL entra como décimo primeiro, pelo MESMO argumento. Ele é
   * o filtro que mais esconde — `?recorte=abertas` some com todas as concluídas
   * — e vive fora dos selects, então quem clicasse em "Limpar" veria a barra
   * inteira zerada e continuaria sem enxergar parte dos registros, sem nada na
   * tela que ligasse uma coisa à outra. O chip "Ver todas" não substitui isto:
   * ele é a saída para quem quer tirar SÓ o recorte e manter o resto, que é o
   * caminho oposto. `bairro` sai junto porque só é lido na montagem — deixá-lo
   * faria um F5 ressuscitar um filtro que o usuário acabou de limpar.
   */
  const limparFiltros = () => {
    setQ('');
    setPrioridadeFiltro('');
    setUfFiltro('');
    setMunicipioFiltro('');
    setBairroFiltro('');
    setTemaFiltro('');
    setStatusFiltro('');
    setResponsavelFiltro('');
    setPrazoFiltro('');
    setPagina(1);
    if (searchParams.has('recorte') || searchParams.has('bairro')) {
      const p = new URLSearchParams(searchParams);
      p.delete('recorte');
      p.delete('bairro');
      setSearchParams(p, { replace: true });
    }
  };

  const { data: todasSolicitacoes = [], isLoading, isError, error } = useQuery({
    queryKey: ['solicitacoes', filtros],
    queryFn: () => solicitacoesApi.list(filtros, token),
    enabled: !!token,
  });

  /**
   * Deep-link da paleta de comandos: `/solicitacoes?solicitacao=<id>` abre a
   * ficha, na mesma convenção de `/contatos?contato=<id>` (Task 5.2).
   * A lista da tela é filtrada, então quando o registro não está nela ele é
   * buscado pelo id — senão o link só funcionaria com os filtros certos.
   */
  const solicitacaoAlvo = searchParams.get('solicitacao') ?? '';
  const solicNaLista = todasSolicitacoes.find((s) => s.id === solicitacaoAlvo);

  const { data: solicPorLink } = useQuery({
    queryKey: ['solicitacoes', 'link', solicitacaoAlvo],
    queryFn: () => solicitacoesApi.get(solicitacaoAlvo, token),
    enabled: !!token && !!solicitacaoAlvo && !solicNaLista,
  });

  const solicDoLink = solicNaLista ?? solicPorLink;

  useEffect(() => {
    if (!solicitacaoAlvo || !solicDoLink) return;
    setSelecionada(solicDoLink);
    const p = new URLSearchParams(searchParams);
    p.delete('solicitacao');
    setSearchParams(p, { replace: true });
    // `searchParams`/`setSearchParams` fora das dependências de propósito:
    // o efeito precisa rodar na chegada do registro, não a cada troca de query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitacaoAlvo, solicDoLink]);

  /**
   * `?nova=1` abre o formulário de nova solicitação já aberto — mesma convenção
   * do `?nova=1` de Ações (`AcoesPage`), que é o que permite ao Resumo ter o
   * botão "Nova solicitação" do protótipo (proto:855, `openDrawer('nova-solic')`).
   *
   * O parâmetro é consumido e apagado da URL com `replace`: sem isso, recarregar
   * a página reabriria o formulário para sempre.
   */
  useEffect(() => {
    if (!searchParams.get('nova')) return;
    setNovaAberta(true);
    const p = new URLSearchParams(searchParams);
    p.delete('nova');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  // O recorte e aplicado no cliente porque a API nao expoe filtro por SLA
  // vencido -- ele depende de comparar o prazo com o instante atual.
  const agora = new Date();

  /**
   * As cinco faixas nomeadas do filtro de prazo, com a mesma aritmética que o
   * cartão e a lista usam para mostrar o prazo — se divergissem, o filtro
   * "Vencidas" traria cartões que a tela pinta como dentro do prazo.
   *
   * "Concluídas" é o único que ignora o relógio; e uma concluída fora do prazo
   * não entra em "Vencidas": acusaria de atraso um atendimento entregue.
   */
  const casaPrazo = (s: Solicitacao) => {
    if (!prazoFiltro) return true;
    if (prazoFiltro === 'conc') return s.status === 'concluida';
    if (s.status === 'concluida') return false;
    const d = diasDeSla(s.sla_prazo);
    if (d === null) return false;
    if (prazoFiltro === 'vencida') return d < 0;
    if (prazoFiltro === 'hoje') return d === 0;
    if (prazoFiltro === '3d') return d > 0 && d <= 3;
    if (prazoFiltro === 'ok') return d > 3;
    return true;
  };

  /**
   * A MESMA regra de território que o servidor aplica ao filtrar solicitações:
   * `LOWER(sol.bairro) = LOWER(:bairro)` (`solicitacoes.service.ts`, `findAll`).
   *
   * `sol.bairro` é copiado de `contato.bairro`, que é texto livre — nada obriga
   * um registro gravado como "centro" a virar "Centro". Comparando com `!==`, o
   * cliente DESCARTAVA justamente o que o servidor acabara de devolver: filtro
   * atendido pelo backend, tela vazia, sem erro nenhum para explicar.
   *
   * Não normaliza acento nem pontuação de propósito. `normalizarTerritorio`
   * (`apps/api/src/core/utils/territorio.util.ts`) faz isso, mas vive no
   * servidor e `solicitacoes.service.ts` NÃO a usa neste filtro — copiar a
   * regra mais forte para cá faria o cliente aceitar o que o servidor nunca
   * devolve, trocando uma divergência por outra.
   */
  const mesmoTerritorio = (a: string | undefined, b: string) =>
    (a ?? '').toLowerCase() === b.toLowerCase();

  const solicitacoes = todasSolicitacoes.filter((s) => {
    // Os recortes dos painéis são aplicados no cliente sobre o mesmo conjunto
    // que alimenta lista, kanban e painéis — é o `L = filtrarSolic()` do
    // protótipo: clicar numa barra recorta a tela inteira, não só o card.
    // `bairro` também é filtrado aqui, e não só no parâmetro da API, para que
    // o recorte valha mesmo quando a rota devolve mais do que foi pedido.
    // UF e município são recortados no cliente porque o `FilterSolicitacaoDto`
    // do servidor só aceita search, bairro, prioridade e responsavel_id.
    if (ufFiltro && s.uf !== ufFiltro) return false;
    if (municipioFiltro && s.municipio !== municipioFiltro) return false;
    if (bairroFiltro && !mesmoTerritorio(s.bairro, bairroFiltro)) return false;
    if (temaFiltro && s.tema !== temaFiltro) return false;
    if (!casaPrazo(s)) return false;
    if (statusFiltro && s.status !== statusFiltro) return false;
    if (responsavelFiltro) {
      const alvo = responsavelFiltro === 'sem' ? '' : responsavelFiltro;
      if ((s.responsavel_id || '') !== alvo) return false;
    }
    if (recorte === 'abertas') return s.status !== 'concluida';
    if (recorte === 'sla_vencido') {
      return s.status !== 'concluida' && !!s.sla_prazo && new Date(s.sla_prazo) < agora;
    }
    // `vencem_hoje` e DISJUNTO de `sla_vencido` de proposito, e o servidor usa
    // o mesmo corte no resumo: o prazo que ja passou da hora e vencido, nao
    // "vence hoje". Somar os dois -- como o prototipo faz com `sla <= 0` --
    // faria o mesmo pedido aparecer nos dois recortes.
    if (recorte === 'vencem_hoje') {
      if (s.status === 'concluida' || !s.sla_prazo) return false;
      const prazo = new Date(s.sla_prazo);
      return prazo >= agora && prazo.toDateString() === agora.toDateString();
    }
    return true;
  });

  const aplicarFiltroDoPainel = (f: FiltroPainel) => {
    setPagina(1);
    if (f.bairro !== undefined) setBairroFiltro(f.bairro);
    if (f.tema !== undefined) setTemaFiltro(f.tema);
    if (f.status !== undefined) setStatusFiltro(f.status);
    if (f.responsavel !== undefined) setResponsavelFiltro(f.responsavel);
  };

  const rotuloRecorte =
    recorte === 'abertas'
      ? 'Somente abertas'
      : recorte === 'sla_vencido'
        ? 'Somente com SLA vencido'
        : recorte === 'vencem_hoje'
          ? 'Somente as que vencem hoje'
          : '';

  const { data: contatosPagina } = useQuery({
    queryKey: ['contatos', 'solicitacoes'],
    queryFn: () => contatosApi.list({ limit: 500 }, token),
    enabled: !!token,
  });
  const contatos = contatosPagina?.items ?? [];

  const { data: liderancas = [] } = useQuery({
    queryKey: ['liderancas', {}],
    queryFn: () => liderancasApi.list({}, token),
    enabled: !!token,
  });

  const { data: usuarios = [] } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => adminApi.listUsuarios(token),
    enabled: !!token,
  });

  // Catálogo de temas do mandato — o MESMO que alimenta o campo Tema das ações
  // parlamentares (`lib/hooks/useTemas.ts`). Sem fonte única, a leitura
  // estratégica dos painéis compara dois vocabulários que nunca se encontram.
  const { temas } = useTemas();

  // Ações do mandato — usadas pela leitura estratégica dos painéis para dizer
  // quais temas com demanda ainda não têm ação em curso.
  const { data: acoes = [] } = useQuery({
    queryKey: ['acoes', {}],
    queryFn: () => acoesApi.list({}, token),
    enabled: !!token,
  });

  /**
   * As réguas do gabinete. A tela precisa saber se a de conclusão está LIGADA
   * antes de prometer ao usuário que o solicitante será avisado: o servidor só
   * dispara quando existe régua ativa para `solicitacao.concluida`
   * (`solicitacoes.service.ts`, `triggerFeedbackRegua`).
   */
  const { data: reguas = [] } = useQuery({
    queryKey: ['reguas'],
    queryFn: () => reguasApi.list(token),
    enabled: !!token,
    staleTime: 5 * 60_000,
  });
  const reguaConclusao = reguas.find(
    (r) => r.gatilho === GATILHO_SOLICITACAO_CONCLUIDA && r.ativa,
  );

  const { data: eventos = [] } = useQuery({
    queryKey: ['solicitacao-eventos', selecionada?.id],
    queryFn: () => solicitacoesApi.getEvents(selecionada!.id, token),
    enabled: !!token && !!selecionada,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['matriz'] });
    queryClient.invalidateQueries({ queryKey: ['falando'] });
    queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
  };

  const criar = useMutation({
    mutationFn: (dados: any) =>
      criarComFilaOffline(() => solicitacoesApi.create(dados, token), {
        metodo: 'POST',
        url: '/gab/solicitacoes',
        corpo: dados,
        rotulo: `Solicitação: ${dados.tema}`,
      }),
    onSuccess: (s) => {
      invalidar();
      // `null` significa que foi para a fila offline: o protocolo ainda não
      // existe, e anunciar um seria inventar número de protocolo.
      toast(s ? `Solicitação ${s.protocolo} registrada.` : 'Solicitação guardada para envio.');
      setNovaAberta(false);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao registrar a solicitação.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) =>
      solicitacoesApi.update(id, dados, token),
    onSuccess: (s, { dados }) => {
      invalidar();
      queryClient.invalidateQueries({ queryKey: ['solicitacao-eventos'] });
      // Concluir pelo select de Situação é a MESMA ação do arraste para
      // "Concluída" e dispara o mesmo gatilho no servidor: tem de dar a mesma
      // resposta sobre o aviso ao solicitante. `alvo` é a ficha ANTES da
      // gravação porque é ela que carrega o `contato_id` de quem pediu.
      const alvo = selecionada;
      toast(
        dados?.status === 'concluida' && alvo
          ? `Solicitação atualizada. ${avisoDeConclusao(alvo).texto}`
          : 'Solicitação atualizada.',
      );
      setSelecionada(s);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a solicitação.')),
  });

  const comentar = useMutation({
    mutationFn: ({ id, texto }: { id: string; texto: string }) =>
      solicitacoesApi.addComentario(id, texto, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitacao-eventos'] });
      toast('Comentário registrado na linha do tempo.');
      setComentario('');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao comentar.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => solicitacoesApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Solicitação removida.');
      setSelecionada(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a solicitação.')),
  });

  const nomeContato = (id?: string) => contatos.find((c) => c.id === id)?.nome || '—';
  const nomeUsuario = (id?: string) => usuarios.find((u) => u.id === id)?.nome || '—';

  const porStatus = (s: Status) => solicitacoes.filter((x) => x.status === s);
  const vencidas = solicitacoes.filter(
    (s) => s.status !== 'concluida' && (diasDeSla(s.sla_prazo) ?? 1) < 0,
  );

  const mudarStatus = (s: Solicitacao, status: Status) =>
    atualizar.mutate({ id: s.id, dados: { status } });

  // ---------------------------------------------- ordenação e paginação (Lista)

  /**
   * Como cada coluna se compara (proto:1184). Solicitante e responsável ordenam
   * pelo NOME exibido, não pelo ULID: ordenar por id daria uma ordem que não
   * tem relação nenhuma com o que a pessoa lê na tela.
   */
  const VALOR_ORDEM: Record<string, (s: Solicitacao) => string | number> = {
    protocolo: (s) => s.protocolo,
    tema: (s) => s.tema,
    territorio: (s) => s.bairro || '',
    solicitante: (s) => nomeContato(s.contato_id),
    responsavel: (s) => nomeUsuario(s.responsavel_id),
    prioridade: (s) => PRIORIDADES.findIndex((p) => p.v === s.prioridade),
    // Sem prazo vai para o fim em vez de valer zero, que o colocaria entre as
    // vencidas.
    sla: (s) => diasDeSla(s.sla_prazo) ?? Number.POSITIVE_INFINITY,
    situacao: (s) => COLUNAS.findIndex((c) => c.v === s.status),
  };

  const ordenadas = ordem
    ? [...solicitacoes].sort((a, b) => {
        const valor = VALOR_ORDEM[ordem.campo];
        if (!valor) return 0;
        const va = valor(a);
        const vb = valor(b);
        const cmp =
          typeof va === 'number' && typeof vb === 'number'
            ? va - vb
            : String(va).localeCompare(String(vb), 'pt-BR');
        return cmp * ordem.dir;
      })
    : solicitacoes;

  const paginas = Math.max(1, Math.ceil(ordenadas.length / POR_PAGINA));
  // Recorte novo pode ter menos páginas que a atual; render fora do intervalo
  // mostraria tabela vazia.
  const paginaAtual = Math.min(pagina, paginas);
  const daPagina = ordenadas.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);

  const ordenarPor = (campo: string) => {
    // `setPagina` fora do updater: um updater de `useState` tem de ser puro, e
    // o StrictMode o invoca duas vezes de propósito para expor efeitos colaterais
    // escondidos ali dentro.
    setPagina(1);
    setOrdem((o) =>
      o?.campo === campo ? { campo, dir: (o.dir * -1) as 1 | -1 } : { campo, dir: 1 },
    );
  };

  const cabecalho = (campo: string, rotulo: string) => (
    <th>
      <button
        type="button"
        className="lnk"
        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer', color: 'inherit' }}
        onClick={() => ordenarPor(campo)}
      >
        {rotulo} {ordem?.campo === campo ? (ordem.dir > 0 ? '↓' : '↑') : ''}
      </button>
    </th>
  );

  // ------------------------------------------------- arraste do kanban (proto:659)

  /**
   * O que a tela pode honestamente prometer ao mover um cartão.
   *
   * O protótipo anuncia aviso ao solicitante em toda movimentação. Aqui o
   * servidor só dispara comunicação em `solicitacao.concluida`, e mesmo assim
   * exige régua ativa, opt-in e telefone — nessa ordem de dependência. Dizer
   * "o solicitante será avisado" ao mover para "Em análise" afirmaria o que o
   * código não faz.
   *
   * Esta é a resposta ÚNICA da tela à pergunta "o solicitante vai ser avisado?".
   * Concluir tem dois caminhos — arrastar o cartão para "Concluída" e escolher
   * "Concluída" no select de Situação do drawer — e os dois executam a mesma
   * requisição e o mesmo gatilho no servidor. Enquanto só um deles consultava
   * as condições reais, a mesma tela dava duas respostas para a mesma pergunta:
   * o drawer prometia o aviso incondicionalmente, o que é falso nos três casos
   * em que o servidor não dispara nada.
   *
   * Devolve o veredito junto com o texto para que quem exibe não precise
   * reinterpretar a frase para saber se houve aviso — reinterpretar seria
   * duplicar a decisão numa segunda regra.
   */
  const avisoDeConclusao = (s: Solicitacao): { havera: boolean; texto: string } => {
    const contato = contatos.find((c) => c.id === s.contato_id);
    if (!contato) {
      return {
        havera: false,
        texto:
          'Solicitante fora da página de contatos carregada: não dá para dizer se haverá aviso.',
      };
    }
    if (!reguaConclusao) {
      return {
        havera: false,
        texto:
          'Sem aviso: nenhuma régua ativa para "solicitação concluída" (Gabinete › Réguas).',
      };
    }
    if (!podeEnviarWhatsApp(contato)) {
      return { havera: false, texto: `Sem aviso ao solicitante: ${motivoBloqueio(contato)}` };
    }
    if (!contato.telefone_e164) {
      return {
        havera: false,
        texto: 'Sem aviso: solicitante com opt-in, mas sem telefone cadastrado.',
      };
    }
    return { havera: true, texto: `Aviso ao solicitante pela régua "${reguaConclusao.nome}".` };
  };

  const mover = useMutation({
    mutationFn: ({ s, status }: { s: Solicitacao; status: Status }) =>
      // Só `status`: `UpdateSolicitacaoDto` não aceita `sla_prazo`, e
      // `gab.solicitacao` não tem coluna `versao` para incrementar.
      solicitacoesApi.update(s.id, { status }, token),
    onSuccess: (_r, { s, status }) => {
      invalidar();
      queryClient.invalidateQueries({ queryKey: ['solicitacao-eventos'] });
      const base = `${s.protocolo} → ${rotuloStatus(status)}.`;
      toast(status === 'concluida' ? `${base} ${avisoDeConclusao(s).texto}` : base);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao mover a solicitação.')),
  });

  const soltarEm = (e: React.DragEvent, status: Status) => {
    e.preventDefault();
    const id = e.dataTransfer?.getData('id');
    const s = solicitacoes.find((x) => x.id === id);
    if (!s || s.status === status) return;
    if (!online) {
      // A fila de `lib/offline.ts` só enfileira CRIAÇÕES, de propósito: ela não
      // resolve conflito de edição, e `gab.solicitacao` não tem versão para
      // resolvê-lo. Enfileirar a movimentação seria prometer uma sincronização
      // que o código não faz.
      toast(
        'Sem conexão: mover cartão exige rede. A fila local só guarda criações: ' +
          'edição não entra na fila porque não há versão de registro para resolver conflito.',
      );
      return;
    }
    mover.mutate({ s, status });
  };

  const cartao = (s: Solicitacao) => {
    const dias = diasDeSla(s.sla_prazo);
    const vencido = s.status !== 'concluida' && dias !== null && dias < 0;

    return (
      <div
        key={s.id}
        className="card"
        draggable
        onDragStart={(e) => e.dataTransfer.setData('id', s.id)}
        style={{
          padding: 10,
          marginBottom: 8,
          cursor: 'pointer',
          borderLeft: `3px solid var(--${vencido ? 'crit' : s.prioridade === 'urgente' ? 'warn' : 'line'})`,
        }}
        onClick={() => setSelecionada(s)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <b className="mono" style={{ fontSize: 11 }}>
            {s.protocolo}
          </b>
          <span className={`chip ${chipPrioridade(s.prioridade as Prioridade)}`} style={{ fontSize: 10 }}>
            {s.prioridade}
          </span>
        </div>
        <div style={{ fontSize: 13, margin: '6px 0 4px' }}>{s.tema}</div>
        <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
          {s.bairro || 'sem território'} · {nomeContato(s.contato_id)}
        </div>
        {s.status !== 'concluida' && dias !== null && (
          <div style={{ fontSize: 11, marginTop: 6, color: vencido ? 'var(--crit)' : 'var(--ink3)' }}>
            {vencido ? `SLA vencido há ${Math.abs(dias)} dia(s)` : `${dias} dia(s) de prazo`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page" id="p-solicitacoes">
      <div className="head">
        <div>
          <h1>Solicitações</h1>
          <p>
            Demandas do cidadão com SLA por categoria, protocolo e linha do tempo auditável de cada
            tramitação. Funciona offline: a criação entra numa fila local com chave de idempotência
            e é reenviada quando a conexão volta.
          </p>
        </div>
        <div className="act">
          <button
            className="btn"
            onClick={() =>
              exportarCsv(
                'solicitacoes',
                [
                  { cabecalho: 'Protocolo', valor: (s: any) => s.protocolo },
                  { cabecalho: 'Tema', valor: (s: any) => s.tema },
                  { cabecalho: 'Território', valor: (s: any) => s.bairro },
                  { cabecalho: 'Prioridade', valor: (s: any) => s.prioridade },
                  { cabecalho: 'Canal', valor: (s: any) => s.canal },
                  { cabecalho: 'Status', valor: (s: any) => s.status },
                  { cabecalho: 'Prazo (SLA)', valor: (s: any) => s.sla_prazo?.slice(0, 10) ?? '' },
                  { cabecalho: 'Descrição', valor: (s: any) => s.descricao },
                  { cabecalho: 'Aberta em', valor: (s: any) => s.created_at?.slice(0, 10) ?? '' },
                ],
                solicitacoes,
              )
            }
            disabled={solicitacoes.length === 0}
            title="Exporta as solicitações como estão filtradas na tela"
          >
            Exportar
          </button>
          <button className="btn pri" onClick={() => setNovaAberta(true)}>
            Nova solicitação
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Abertas</div>
          <div className="v">{solicitacoes.filter((s) => s.status !== 'concluida').length}</div>
          <div className="d">de {solicitacoes.length} no total</div>
        </div>
        <div className="card kpi">
          <div className="l">SLA vencido</div>
          <div className="v" style={{ color: vencidas.length ? 'var(--crit)' : 'var(--good)' }}>
            {vencidas.length}
          </div>
          <div className="d">sem conclusão após o prazo</div>
        </div>
        <div className="card kpi">
          <div className="l">Concluídas</div>
          <div className="v" style={{ color: 'var(--good)' }}>{porStatus('concluida').length}</div>
          <div className="d">
            {solicitacoes.length
              ? Math.round((porStatus('concluida').length / solicitacoes.length) * 100)
              : 0}
            % de taxa de retorno
          </div>
        </div>
        <div className="card kpi">
          <div className="l">Territórios atingidos</div>
          <div className="v">{new Set(solicitacoes.map((s) => s.bairro).filter(Boolean)).size}</div>
          <div className="d">de {bairros.length} do mandato</div>
        </div>
      </div>

      {/* Filtros */}
      {rotuloRecorte && (
        <div className="filters" style={{ marginBottom: 4 }}>
          <span className="chip warn">
            {rotuloRecorte} · {solicitacoes.length} de {todasSolicitacoes.length}
          </span>
          <button
            className="btn sm"
            onClick={() => {
              // Limpa so o recorte; os demais filtros da tela continuam.
              const p = new URLSearchParams(searchParams);
              p.delete('recorte');
              setSearchParams(p, { replace: true });
            }}
          >
            Ver todas
          </button>
        </div>
      )}

      <div className="filters">
        <input
          className="in"
          style={{ width: 260 }}
          placeholder="Buscar por protocolo, tema ou descrição..."
          value={q}
          onChange={(e) => aoFiltrar(setQ)(e.target.value)}
        />
        <select
          className="sel"
          aria-label="Estado"
          value={ufFiltro}
          onChange={(e) => trocarUf(e.target.value)}
        >
          <option value="">Todos os estados</option>
          {ufs.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
        <select
          className="sel"
          aria-label="Município"
          value={municipioFiltro}
          onChange={(e) => trocarMunicipio(e.target.value)}
        >
          <option value="">Todos os municípios</option>
          {municipios.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          className="sel"
          aria-label="Território"
          value={bairroFiltro}
          onChange={(e) => aoFiltrar(setBairroFiltro)(e.target.value)}
        >
          <option value="">Todos os territórios</option>
          {territorios.map((b) => (
            <option key={b.id} value={b.n}>
              {b.n}
            </option>
          ))}
        </select>
        <select
          className="sel"
          aria-label="Tema"
          value={temaFiltro}
          onChange={(e) => aoFiltrar(setTemaFiltro)(e.target.value)}
        >
          <option value="">Todos os temas</option>
          {temas.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className="sel"
          aria-label="Status"
          value={statusFiltro}
          onChange={(e) => aoFiltrar(setStatusFiltro)(e.target.value)}
        >
          <option value="">Todos os status</option>
          {COLUNAS.map((c) => (
            <option key={c.v} value={c.v}>
              {c.l}
            </option>
          ))}
        </select>
        <select
          className="sel"
          aria-label="Responsável"
          value={responsavelFiltro}
          onChange={(e) => aoFiltrar(setResponsavelFiltro)(e.target.value)}
        >
          <option value="">Todos os responsáveis</option>
          <option value="sem">Sem responsável</option>
          {usuarios.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome}
            </option>
          ))}
        </select>
        <select
          className="sel"
          aria-label="Prazo"
          value={prazoFiltro}
          onChange={(e) => aoFiltrar(setPrazoFiltro)(e.target.value)}
        >
          <option value="">Qualquer prazo</option>
          {PRAZOS.map((p) => (
            <option key={p.v} value={p.v}>
              {p.l}
            </option>
          ))}
        </select>
        <select
          className="sel"
          aria-label="Prioridade"
          value={prioridadeFiltro}
          onChange={(e) => aoFiltrar(setPrioridadeFiltro)(e.target.value)}
        >
          <option value="">Todas as prioridades</option>
          {PRIORIDADES.map((p) => (
            <option key={p.v} value={p.v}>
              {p.l}
            </option>
          ))}
        </select>
        <button className="btn sm" onClick={limparFiltros}>
          Limpar
        </button>
        <span style={{ flex: 1 }} />
        <span
          className={`chip neutral chipbtn ${view === 'kanban' ? 'on' : ''}`}
          onClick={() => setView('kanban')}
        >
          Kanban
        </span>
        <span
          className={`chip neutral chipbtn ${view === 'lista' ? 'on' : ''}`}
          onClick={() => setView('lista')}
        >
          Lista
        </span>
        <span
          className={`chip neutral chipbtn ${view === 'paineis' ? 'on' : ''}`}
          onClick={() => setView('paineis')}
        >
          Painéis
        </span>
      </div>

      {/* Recortes escritos pelos painéis: visíveis e removíveis um a um. */}
      {(temaFiltro || statusFiltro || responsavelFiltro) && (
        <div className="filters" style={{ marginTop: 4 }}>
          {temaFiltro && (
            <span className="chip neutral chipbtn on" onClick={() => setTemaFiltro('')}>
              Tema: {temaFiltro} ✕
            </span>
          )}
          {statusFiltro && (
            <span className="chip neutral chipbtn on" onClick={() => setStatusFiltro('')}>
              Status: {rotuloStatus(statusFiltro as Status)} ✕
            </span>
          )}
          {responsavelFiltro && (
            <span className="chip neutral chipbtn on" onClick={() => setResponsavelFiltro('')}>
              Responsável:{' '}
              {responsavelFiltro === 'sem' ? 'sem responsável' : nomeUsuario(responsavelFiltro)} ✕
            </span>
          )}
        </div>
      )}

      {isLoading && <div className="empty">Carregando solicitações...</div>}
      {isError && !isLoading && (
        <div className="empty">
          Não foi possível carregar: {erroDe(error, 'erro de comunicação com a API.')}
        </div>
      )}

      {!isLoading && !isError && view !== 'paineis' && solicitacoes.length === 0 && (
        <div className="empty">
          Nenhuma solicitação registrada. Use <b>Nova solicitação</b>: ela precisa de um contato
          cadastrado como solicitante.
        </div>
      )}

      {/* Kanban */}
      {!isLoading && solicitacoes.length > 0 && view === 'kanban' && (
        <>
          <div className="grid g4" style={{ marginTop: 12, alignItems: 'start' }}>
            {COLUNAS.map((col) => (
              <div
                key={col.v}
                className="card flat"
                data-coluna={col.v}
                // `onDragOver` com `preventDefault` é o que torna a coluna um
                // destino válido: sem ele o navegador recusa o drop e o cartão
                // volta sozinho, sem erro nenhum.
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => soltarEm(e, col.v)}
                style={{ background: 'var(--surface2)', padding: 10 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <b style={{ fontSize: 13 }}>{col.l}</b>
                  <span className="chip neutral">{porStatus(col.v).length}</span>
                </div>
                {porStatus(col.v).map(cartao)}
                {porStatus(col.v).length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--ink3)', padding: '8px 0' }}>Vazio</div>
                )}
              </div>
            ))}
          </div>

          {!online && (
            <div className="note warn" style={{ marginTop: 10, fontSize: 12 }}>
              <b>Sem conexão: mover cartão não funciona agora.</b> A fila local de{' '}
              <code>lib/offline.ts</code> só guarda criações (nova solicitação, contato, visita).
              Edição não entra na fila porque ela não resolve conflito entre dois aparelhos.
            </div>
          )}

          {/*
            Fica visível: duas pessoas movendo o mesmo cartão não recebem aviso
            de conflito, e quem não souber disso confia no que está na tela.
          */}
          <div className="note warn" style={{ marginTop: 10, fontSize: 12 }}>
            <b>Mover cartão não versiona o registro:</b> se duas pessoas moverem o mesmo cartão,
            vale a última gravação, sem aviso.{' '}
            <Info titulo="Por que não há controle de conflito ao mover">
              Em <code>gab.solicitacao</code> não existe coluna <code>versao</code>: ela existe em{' '}
              <code>gab.agenda_evento</code> e em <code>oto.persona</code>, não aqui. O prazo de
              SLA também não é apagado ao concluir (<code>UpdateSolicitacaoDto</code> não aceita{' '}
              <code>sla_prazo</code>): a tela para de contá-lo, o registro guarda a data limite
              original.
            </Info>
          </div>
        </>
      )}

      {/* Lista */}
      {!isLoading && solicitacoes.length > 0 && view === 'lista' && (
        <div className="card flat tw" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                {cabecalho('protocolo', 'Protocolo')}
                {cabecalho('tema', 'Tema')}
                {cabecalho('territorio', 'Território')}
                {cabecalho('solicitante', 'Solicitante')}
                {cabecalho('responsavel', 'Responsável')}
                {cabecalho('prioridade', 'Prioridade')}
                {cabecalho('sla', 'SLA')}
                {cabecalho('situacao', 'Situação')}
              </tr>
            </thead>
            <tbody>
              {daPagina.map((s) => {
                const dias = diasDeSla(s.sla_prazo);
                const vencido = s.status !== 'concluida' && dias !== null && dias < 0;
                return (
                  <tr key={s.id} className="row" style={{ cursor: 'pointer' }} onClick={() => setSelecionada(s)}>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {s.protocolo}
                    </td>
                    <td>
                      <b>{s.tema}</b>
                    </td>
                    <td>{s.bairro || '—'}</td>
                    <td>{nomeContato(s.contato_id)}</td>
                    <td>{nomeUsuario(s.responsavel_id)}</td>
                    <td>
                      <span className={`chip ${chipPrioridade(s.prioridade as Prioridade)}`}>
                        {s.prioridade}
                      </span>
                    </td>
                    <td style={{ color: vencido ? 'var(--crit)' : undefined }}>
                      {s.status === 'concluida' ? '—' : dias === null ? '—' : `${dias}d`}
                    </td>
                    <td>
                      <span className={`chip ${s.status === 'concluida' ? 'good' : 'neutral'}`}>
                        {rotuloStatus(s.status as Status)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="pager">
            <span style={{ marginRight: 'auto' }}>
              {ordenadas.length} solicitações · página {paginaAtual} de {paginas}
            </span>
            <button
              aria-label="Página anterior"
              disabled={paginaAtual === 1}
              onClick={() => setPagina(Math.max(1, paginaAtual - 1))}
            >
              ‹
            </button>
            {/* As páginas vizinhas, a primeira e a última (proto:1184): com 90
                páginas, listar todas empurraria a tabela para fora da tela. */}
            {Array.from({ length: paginas }, (_, i) => i + 1)
              .filter((i) => Math.abs(i - paginaAtual) <= 3 || i === 1 || i === paginas)
              .map((i) => (
                <button
                  key={i}
                  className={paginaAtual === i ? 'on' : ''}
                  aria-current={paginaAtual === i ? 'page' : undefined}
                  onClick={() => setPagina(i)}
                >
                  {i}
                </button>
              ))}
            <button
              aria-label="Próxima página"
              disabled={paginaAtual === paginas}
              onClick={() => setPagina(Math.min(paginas, paginaAtual + 1))}
            >
              ›
            </button>
          </div>
        </div>
      )}

      {/* Painéis (proto:1201-1208) */}
      {!isLoading && !isError && view === 'paineis' && (
        <PainelSolicitacoes
          solicitacoes={solicitacoes}
          contatos={contatos}
          usuarios={usuarios}
          acoes={acoes}
          onFiltrar={aplicarFiltroDoPainel}
          onAbrirContato={(id) => navigate(`/contatos?contato=${id}`)}
          onCriarAcao={(tema) => navigate(`/acoes?nova=1${tema ? `&tema=${encodeURIComponent(tema)}` : ''}`)}
        />
      )}

      {/* Drawer: ficha */}
      {selecionada && (
        <Drawer
          aberto
          titulo={selecionada.protocolo}
          aoFechar={() => setSelecionada(null)}
        >
              <h3 style={{ margin: '0 0 8px' }}>{selecionada.tema}</h3>
              <p style={{ fontSize: 13, color: 'var(--ink2)' }}>{selecionada.descricao}</p>

              <dl className="dl" style={{ marginTop: 12 }}>
                <dt>Solicitante</dt>
                <dd>{nomeContato(selecionada.contato_id)}</dd>
                <dt>Território</dt>
                <dd>{selecionada.bairro || '—'}</dd>
                <dt>Canal</dt>
                <dd>{selecionada.canal}</dd>
                <dt>
                  Data prevista (prazo de SLA){' '}
                  <Info titulo="Por que a data prevista não é editável aqui">
                    A coluna existe (<span className="mono">gab.solicitacao.sla_prazo</span>) e é
                    definida na abertura, mas <span className="mono">UpdateSolicitacaoDto</span> não
                    declara o campo e a API roda com{' '}
                    <span className="mono">ValidationPipe(&#123; whitelist: true &#125;)</span>: um
                    campo de data aqui enviaria o valor e o servidor o descartaria sem erro nenhum.
                  </Info>
                </dt>
                <dd>{formatarData(selecionada.sla_prazo)}</dd>
                <dt>Aberta em</dt>
                <dd>{formatarData(selecionada.created_at)}</dd>
              </dl>

              {/* Tramitação */}
              <div className="grid g2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Situação</label>
                  <select
                    className="in"
                    aria-label="Situação"
                    value={selecionada.status}
                    disabled={atualizar.isPending}
                    onChange={(e) => mudarStatus(selecionada, e.target.value as Status)}
                  >
                    {COLUNAS.map((c) => (
                      <option key={c.v} value={c.v}>
                        {c.l}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Prioridade</label>
                  <select
                    className="in"
                    value={selecionada.prioridade}
                    disabled={atualizar.isPending}
                    onChange={(e) =>
                      atualizar.mutate({
                        id: selecionada.id,
                        dados: { prioridade: e.target.value },
                      })
                    }
                  >
                    {PRIORIDADES.map((p) => (
                      <option key={p.v} value={p.v}>
                        {p.l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label>Responsável</label>
                <select
                  className="in"
                  value={selecionada.responsavel_id || ''}
                  disabled={atualizar.isPending}
                  onChange={(e) =>
                    atualizar.mutate({
                      id: selecionada.id,
                      dados: { responsavel_id: e.target.value || undefined },
                    })
                  }
                >
                  <option value="">Sem responsável</option>
                  {usuarios.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* A MESMA resposta que o arraste dá — `avisoDeConclusao`, não uma
                  promessa fixa. O protótipo afirmava aqui que a régua "é
                  disparada automaticamente"; é falso quando não há régua ativa,
                  quando o solicitante não tem base legal e quando não tem
                  telefone. */}
              {selecionada.status === 'concluida' &&
                (() => {
                  const aviso = avisoDeConclusao(selecionada);
                  return (
                    <div
                      className={`note ${aviso.havera ? 'good' : 'warn'}`}
                      style={{ marginTop: 10, fontSize: 12 }}
                    >
                      {aviso.texto}
                    </div>
                  );
                })()}

              <Anexos
                donoTipo="solicitacao"
                donoId={selecionada.id}
                tipoPadrao="Relatório público"
                titulo="Documentos e evidências"
                ajuda="Fotos do local, ofícios, laudos e o que mais sustente o encaminhamento."
              />

              {/* Linha do tempo */}
              <h3 style={{ marginTop: 18 }}>
                Linha do tempo{' '}
                <Info titulo="O que a ficha registra de histórico">
                  Esta linha do tempo é o histórico de mudanças da ficha, e é dado real.{' '}
                  <span className="mono">gab.solicitacao</span> não tem coluna de versão:{' '}
                  <span className="mono">versao</span> existe em{' '}
                  <span className="mono">gab.agenda_evento</span> e{' '}
                  <span className="mono">oto.persona</span>, e nas duas quem incrementa é o
                  servidor.
                  <br />
                  Também não há vínculo entre ação e solicitação:{' '}
                  <span className="mono">gab.acao</span> não tem{' '}
                  <span className="mono">solicitacao_id</span>,{' '}
                  <span className="mono">gab.solicitacao</span> não tem{' '}
                  <span className="mono">acao_id</span> e não há tabela de ligação. O campo
                  "Solicitações resolvidas" da ficha da ação é uma anotação digitada.
                </Info>
              </h3>
              <div className="tl" style={{ marginTop: 8 }}>
                {eventos.map((ev) => (
                  <div key={ev.id} className="tli">
                    <div className="d">{formatarData(ev.created_at)}</div>
                    <div className="t">
                      <b>{ev.tipo.replace(/_/g, ' ')}</b>
                    </div>
                    <div className="desc">{ev.descricao}</div>
                  </div>
                ))}
                {eventos.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--ink3)' }}>Sem eventos registrados.</div>
                )}
              </div>

              <div className="field" style={{ marginTop: 14 }}>
                <label>Novo comentário</label>
                <textarea
                  className="in"
                  style={{ minHeight: 70 }}
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Registra na linha do tempo, com autor e data"
                />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  className="btn pri"
                  disabled={comentar.isPending || !comentario.trim()}
                  onClick={() => comentar.mutate({ id: selecionada.id, texto: comentario.trim() })}
                >
                  {comentar.isPending ? 'Registrando...' : 'Comentar'}
                </button>
                {/*
                  "Enviar retorno" do protótipo (proto:1226) — com o caminho que
                  este sistema tem, não com o que o protótipo simula.

                  O protótipo grava "Retorno enviado ao solicitante pela régua
                  Status". Aqui não existe rota para disparar régua à mão:
                  `reguasApi` é CRUD, e o servidor só dispara
                  `solicitacao.concluida` sozinho, dentro do update de status. O
                  que existe de verdade é o mesmo caminho manual das outras
                  telas — abrir o WhatsApp do solicitante e registrar o retorno
                  na linha do tempo, que é `POST /comentarios`, dado real com
                  autor e data. A LGPD vale igual: sem opt-in, não há botão.
                */}
                {(() => {
                  const contato = contatos.find((c) => c.id === selecionada.contato_id);
                  const bloqueio = contato ? motivoBloqueio(contato) : 'Solicitante fora da página de contatos carregada.';
                  const liberado = !!contato && podeEnviarWhatsApp(contato) && !!contato.telefone_e164;
                  const impedimento = !contato
                    ? 'Solicitante fora da página de contatos carregada.'
                    : bloqueio || (!contato.telefone_e164 ? 'Solicitante com opt-in, mas sem telefone cadastrado.' : null);
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <button
                        className="btn"
                        disabled={!liberado || comentar.isPending}
                        onClick={() => {
                          if (!liberado || !contato) return;
                          const texto = `Olá ${contato.nome}, retorno sobre a solicitação ${selecionada.protocolo}: `;
                          window.open(
                            `https://wa.me/${contato.telefone_e164!.replace(/\D/g, '')}?text=${encodeURIComponent(texto)}`,
                            '_blank',
                          );
                          comentar.mutate({
                            id: selecionada.id,
                            texto: `Retorno ao solicitante aberto por WhatsApp (${contato.nome}).`,
                          });
                        }}
                      >
                        Enviar retorno
                      </button>
                      {/*
                        Uma mensagem, não duas. Quando a ficha está concluída, o
                        aviso de `avisoDeConclusao` logo acima já diz exatamente
                        o mesmo motivo — repeti-lo aqui só faria o usuário ler a
                        mesma frase de novo.
                      */}
                      {impedimento && selecionada.status !== 'concluida' && (
                        <small style={{ fontSize: 11, color: 'var(--warn)' }}>{impedimento}</small>
                      )}
                    </span>
                  );
                })()}
                <button className="btn" onClick={() => navigate('/acoes')}>
                  Abrir ações do mandato
                </button>
                <button
                  className="btn"
                  style={{ color: 'var(--crit)', marginLeft: 'auto' }}
                  disabled={excluir.isPending}
                  onClick={() => {
                    if (window.confirm(`Remover a solicitação ${selecionada.protocolo}?`)) {
                      excluir.mutate(selecionada.id);
                    }
                  }}
                >
                  Excluir
                </button>
              </div>
        </Drawer>
      )}

      {/* Drawer: nova */}
      {novaAberta && (
        <NovaSolicitacao
          contatos={contatos}
          liderancas={liderancas}
          usuarios={usuarios}
          temas={temas}
          salvando={criar.isPending}
          onCancelar={() => setNovaAberta(false)}
          onSalvar={(dados) => criar.mutate(dados)}
        />
      )}
    </div>
  );
}

function NovaSolicitacao({
  contatos,
  liderancas,
  usuarios,
  temas,
  salvando,
  onCancelar,
  onSalvar,
}: {
  contatos: Array<{ id: string; nome: string; bairro?: string }>;
  liderancas: Array<{ id: string; nome: string }>;
  usuarios: Array<{ id: string; nome: string }>;
  temas: string[];
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: any) => void;
}) {
  const [contatoId, setContatoId] = useState('');
  const [tema, setTema] = useState(temas[0] || '');
  const [descricao, setDescricao] = useState('');
  const [prioridade, setPrioridade] = useState<Prioridade>('media');
  const [canal, setCanal] = useState<(typeof CANAIS)[number]>('presencial');
  const [liderancaId, setLiderancaId] = useState('');
  const [responsavelId, setResponsavelId] = useState('');

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contatoId) {
      toast('Selecione o contato solicitante. O território vem da ficha dele.');
      return;
    }
    if (!tema.trim() || !descricao.trim()) {
      toast('Tema e descrição são obrigatórios.');
      return;
    }
    onSalvar({
      contato_id: contatoId,
      tema: tema.trim(),
      descricao: descricao.trim(),
      prioridade,
      canal,
      lideranca_origem_id: liderancaId || undefined,
      responsavel_id: responsavelId || undefined,
    });
  };

  return (
    <Drawer aberto titulo="Nova solicitação" aoFechar={onCancelar}>
      <form onSubmit={submeter} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label>Solicitante *</label>
              <select className="in" value={contatoId} onChange={(e) => setContatoId(e.target.value)} required>
                <option value="">Selecione o contato</option>
                {contatos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome} {c.bairro ? `· ${c.bairro}` : ''}
                  </option>
                ))}
              </select>
              <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                O território da solicitação é herdado da ficha do contato.
              </small>
            </div>

            <div className="field">
              <label>Tema *</label>
              <input
                className="in"
                list="temas-solicitacao"
                value={tema}
                onChange={(e) => setTema(e.target.value)}
                placeholder="Categoria da demanda"
                required
              />
              <datalist id="temas-solicitacao">
                {temas.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>

            <div className="field">
              <label>Descrição *</label>
              <textarea
                className="in"
                style={{ minHeight: 100 }}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="O que a pessoa pediu, com o detalhe necessário para encaminhar"
                required
              />
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Prioridade</label>
                <select
                  className="in"
                  value={prioridade}
                  onChange={(e) => setPrioridade(e.target.value as Prioridade)}
                >
                  {PRIORIDADES.map((p) => (
                    <option key={p.v} value={p.v}>
                      {p.l}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Canal de entrada</label>
                <select className="in" value={canal} onChange={(e) => setCanal(e.target.value as any)}>
                  {CANAIS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Liderança de origem</label>
                <select className="in" value={liderancaId} onChange={(e) => setLiderancaId(e.target.value)}>
                  <option value="">Nenhuma</option>
                  {liderancas.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Responsável</label>
                <select
                  className="in"
                  value={responsavelId}
                  onChange={(e) => setResponsavelId(e.target.value)}
                >
                  <option value="">Definir depois</option>
                  {usuarios.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>


            <div style={{ display: 'flex', gap: 10, marginTop: 6, alignItems: 'center' }}>
              <button type="button" className="btn" onClick={onCancelar}>
                Cancelar
              </button>
              <button type="submit" className="btn pri" disabled={salvando}>
                {salvando ? 'Registrando...' : 'Criar solicitação'}
              </button>
              <Info titulo="O que o servidor gera ao criar">
                O protocolo e o prazo de SLA são gerados pelo servidor. O prazo usa o padrão
                configurado em <b>Admin › Limiares</b>.
              </Info>
            </div>
      </form>
    </Drawer>
  );
}
